<script lang="ts">
import { computed, defineComponent, h, nextTick, ref, shallowRef, unref } from 'vue';

import { classNames, createRange, keyNames } from '@components/utils';

import {
  makeDisabledProps,
  makeTagProps,
  makeThemeProps,
  provideTheme,
  useDisplay,
  useProxyModel,
  useRefs,
  useRender,
  useResizeObserver
} from '../../composable';
import { CdsFunctionalButton } from '../CdsButton';
import { CdsTextInput } from '../CdsTextInput';

import type { PropType, ComponentPublicInstance } from 'vue';

export const componentName = 'CdsPagination';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Начальная страница
     */
    start: {
      type: [Number, String] as PropType<number | string>,
      default: 1
    },
    /**
     * Количество страниц
     */
    length: {
      type: [Number, String] as PropType<number | string>,
      default: 1,
      validator: (value: number) => value % 1 === 0
    },
    /**
     * Количество видимых страниц
     */
    totalVisible: {
      type: [Number, String] as PropType<number | string>,
      default: undefined
    },
    /**
     * Текст отображаемые между страницами при сокрытии
     */
    ellipsis: {
      type: String,
      default: '...'
    },
    /**
     * Включает отображение поля для ввода номера страницы
     */
    quickJumper: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Значение v-model
     */
    modelValue: {
      type: Number as PropType<number>,
      default: (props: any) => props.start as number
    },
    ...makeTagProps({
      tag: 'nav'
    }),
    ...makeDisabledProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие изменения значения v-model
     */
    'update:modelValue': (value?: number) => true,
    /**
     * Событие перехода на первую страницу
     */
    first: (value: number) => true,
    /**
     * Событие перехода на последнюю страницу
     */
    last: (value: number) => true,
    /**
     * Событие перехода на следующую страницу
     */
    next: (value: number) => true,
    /**
     * Событие перехода на предыдущую страницу
     */
    prev: (value: number) => true,
    /**
     * Событие перехода с помощью поля ввода номера страницы
     */
    quickJump: (value: number) => true
  },
  setup(props, { slots, emit, expose }) {
    const { themeClasses } = provideTheme(props);
    const page = useProxyModel(props, 'modelValue');
    const { width } = useDisplay();
    const maxButtons = shallowRef(-1);
    const inputPageModel = ref(null);

    const { refs, updateRef } = useRefs<ComponentPublicInstance>();

    const { resizeRef } = useResizeObserver((entries: ResizeObserverEntry[]) => {
      if (!entries.length) return;

      const { target, contentRect } = entries[0];
      const firstItem = target.querySelector('.cds-pagination__list > *') as HTMLElement;

      if (!firstItem) return;

      const totalWidth = contentRect.width;
      const itemWidth = firstItem.offsetWidth + parseFloat(getComputedStyle(firstItem).marginRight) * 2;

      maxButtons.value = getMax(totalWidth, itemWidth);
    });

    const length = computed(() => parseInt(props.length as unknown as string, 10));
    const start = computed(() => parseInt(props.start as unknown as string, 10));

    const classes = computed(() => {
      return {
        'cds-pagination': true,
        'cds-pagination--quick-jumper': props.quickJumper,
        'cds-pagination--disabled': props.disabled
      };
    });

    const listClasses = computed(() => {
      return {
        'cds-pagination__list': true
      };
    });

    const totalVisible = computed(() => {
      if (props.totalVisible) {
        return parseInt(props.totalVisible as unknown as string, 10);
      } else if (unref(maxButtons) >= 0) {
        return unref(maxButtons);
      } else {
        return getMax(unref(width), 32);
      }
    });

    const range = computed(() => {
      if (unref(length) <= 0 || isNaN(unref(length)) || unref(length) > Number.MAX_SAFE_INTEGER) return [];
      if (unref(totalVisible) <= 1) return [unref(page)];
      if (unref(length) <= unref(totalVisible)) return createRange(unref(length), unref(start));

      const even = unref(totalVisible) % 2 === 0;
      const middle = even ? unref(totalVisible) / 2 : Math.floor(unref(totalVisible) / 2);
      const left = even ? middle : middle + 1;
      const right = unref(length) - middle;

      if (left - unref(page) >= 0) {
        return [...createRange(Math.max(1, unref(totalVisible) - 1), unref(start)), props.ellipsis, unref(length)];
      } else if (unref(page) - right >= (even ? 1 : 0)) {
        const rangeLength = unref(totalVisible) - 1;
        const rangeStart = unref(length) - rangeLength + unref(start);
        return [unref(start), props.ellipsis, ...createRange(rangeLength, rangeStart)];
      } else {
        const rangeLength = Math.max(1, unref(totalVisible) - 3);
        const rangeStart = rangeLength === 1 ? unref(page) : unref(page) - Math.ceil(rangeLength / 2) + unref(start);
        return [unref(start), props.ellipsis, ...createRange(rangeLength, rangeStart), props.ellipsis, unref(length)];
      }
    });
    const items = computed(() => {
      return unref(range).map((item, index) => {
        const ref = (el: any) => updateRef(el, index);

        if (typeof item === 'string') {
          return {
            isActive: false,
            key: `ellipsis-${index}`,
            page: item,
            props: {
              ref,
              readonly: true,
              class: 'cds-pagination__ellipsis'
            }
          };
        } else {
          const isActive = item === unref(page);

          return {
            isActive,
            key: item,
            page: item,
            props: {
              ref,
              disabled: props.disabled || +props.length < 2 || isActive,
              onClick: () => setValue(item)
            }
          };
        }
      });
    });

    const controls = computed(() => {
      const prevDisabled = props.disabled || unref(page) <= unref(start);
      const nextDisabled = props.disabled || unref(page) >= unref(start) + unref(length) - 1;

      return {
        prev: {
          icon: 'chevron-left',
          disabled: prevDisabled,
          onClick: setPrev
        },
        next: {
          icon: 'chevron-right',
          disabled: nextDisabled,
          onClick: setNext
        }
      };
    });

    function getMax(totalWidth: number, itemWidth: number) {
      const minButtons = 3;
      return Math.max(0, Math.floor(+((totalWidth - itemWidth * minButtons) / itemWidth).toFixed(2)));
    }

    function updateFocus() {
      const currentIndex = unref(page) - unref(start);
      unref(refs)[currentIndex]?.$el.focus();
    }

    function onKeydown(event: KeyboardEvent) {
      if (event.key === keyNames.left && !props.disabled && unref(page) > +props.start) {
        page.value = page.value - 1;
        nextTick(updateFocus);
      } else if (event.key === keyNames.right && !props.disabled && unref(page) < unref(start) + unref(length) - 1) {
        page.value = page.value + 1;
        nextTick(updateFocus);
      }
    }

    function setValue(value: number, reason?: any) {
      page.value = value;
      reason && emit(reason, value);
    }

    function setFirst() {
      setValue(unref(start), 'first');
    }

    function setLast() {
      setValue(unref(start) + unref(length), 'last');
    }

    function setNext() {
      setValue(unref(page) + 1, 'next');
    }

    function setPrev() {
      setValue(unref(page) - 1, 'prev');
    }

    function onQuickJumpToPage(event: Event) {
      event.preventDefault();

      if (!unref(inputPageModel)) return;
      setValue(Number(unref(inputPageModel)), 'quickJump');

      inputPageModel.value = null;
    }

    useRender(() => {
      return h(
        props.tag,
        { ref: resizeRef, class: classNames(unref(classes), unref(themeClasses)), role: 'navigation', onKeydown },
        [
          h('ul', { class: unref(listClasses) }, [
            h(
              'li',
              { key: 'prev', class: 'cds-pagination__prev' },
              slots.prev ? slots.prev(unref(controls).prev) : h(CdsFunctionalButton, unref(controls).prev as {})
            ),
            ...unref(items).map((item) =>
              h(
                'li',
                {
                  key: item.key,
                  class: classNames('cds-pagination__item', { 'cds-pagination__item--active': item.isActive })
                },
                slots.item ? slots.item(item) : h(CdsFunctionalButton, item.props, { default: () => item.page })
              )
            ),
            h(
              'li',
              { key: 'prev', class: 'cds-pagination__next' },
              slots.next ? slots.next(unref(controls).next) : h(CdsFunctionalButton, unref(controls).next)
            )
          ]),
          slots.append?.(),
          props.quickJumper &&
            h('div', { class: 'cds-pagination__controls' }, [
              h('span', { class: 'cds-pagination__controls-page' }, 'Перейти к странице'),
              h(
                'form',
                { onSubmit: onQuickJumpToPage },
                h(CdsTextInput, {
                  modelValue: inputPageModel,
                  disabled: props.disabled,
                  hideMessages: true,
                  type: 'number',
                  onBlur: onQuickJumpToPage
                })
              )
            ])
        ]
      );
    });

    expose({
      setFirst,
      setLast,
      setNext,
      setPrev
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsPagination';
</style>
